import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PagedCollectionFilterResponseWrapper } from '../../classes/core/api/paged-collection-filter-response-wrapper';
import { IFilter } from '../../interfaces/filters/filter';
import { IFilterConfig } from '../../interfaces/filters/filter-config';
import { IFilterOption } from '../../interfaces/filters/filter-option';
import { ISort } from '../../interfaces/filters/sort/sort';
import { BaseApiService } from '../core/base-api.service';
import { SearchApiService } from '../search/search-api.service';

/* 
Any injector used so each lazy loaded module has its own instance of this service
*/
@Injectable({
    providedIn: 'any'
})
export class FilterService {
    private _filterConfig = new BehaviorSubject<IFilterConfig>(null);
    private _data = new BehaviorSubject<any[]>(null);
    private _totalResults = new BehaviorSubject<number>(null);
    private _dataLoading = new BehaviorSubject<boolean>(false);

    constructor(
        private searchApiService: SearchApiService,
        private baseApiService: BaseApiService
    ) {}

    /* 
    Deselect filter from config and all its options / potential extra filters
    */
    public removeFilter(filter: IFilter): void {
        const currentVal = this.filterConfig;
        const filterVal = currentVal.filters.find(e => e.field == filter.field);
        if (filterVal) {
            filterVal.selected = false;
            filterVal.options?.forEach(x => (x.isSelected = false));
            filterVal.extraFilters?.forEach(x => {
                x.selected = false;
                x.options?.forEach(c => (c.isSelected = false));
            });
            this._filterConfig.next(currentVal);
        }
    }

    /* 
    Deselects all filters and sorts
    */
    public reset(): void {
        const filterVal = this.filterConfig;
        filterVal.filters?.forEach(e => {
            e.selected = false;
            e.options?.forEach(e => (e.isSelected = false));
            e.extraFilters?.forEach(e => {
                e.selected = false;
                e.options?.forEach(x => (x.isSelected = false));
            });
        });

        filterVal.sorts?.forEach(e => {
            e.selected = false;
        });
        this._filterConfig.next(filterVal);
        this._data.next(null);
        this._totalResults.next(null);
    }

    /* 
   Query generation for api, conforms to api rules for receiving data
   */
    public generateQuery(): string {
        let query = ``;
        query = this.generateSortQuery(query);
        query = this.generateFilterQuery(query);
        query = this.generateSearchQuery(query);
        query = this.generatePaginationQuery(query);
        return query;
    }

    /* 
  Getting the most recent allocated filter
  */
    public mostRecentFilter(): IFilter {
        let currentVal = this._filterConfig.value;
        if (currentVal) {
            let addedFilters = currentVal.filters?.filter(e => e.added);
            if (addedFilters?.length > 0) {
                return addedFilters.reduce((a, b) =>
                    a.added > b.added ? a : b
                );
            }
        }
    }

    /* 
    Used to merge existing filters with new filter options received (dynamically change in tree like structure)
    Does not replace the options of most recently added filter / extra filter, in case user wants to change option
    */
    private refreshFilters(newFilters: IFilter[]): IFilterConfig {
        const currentVal = this.filterConfig;
        if (currentVal?.filters) {
            newFilters.forEach(e => {
                let filter = currentVal.filters.find(x => x.field == e.field);
                if (filter) {
                    e.options?.forEach(o => {
                        let option = filter.options?.find(
                            x => x.value == o.value
                        );
                        if (option) {
                            o.isSelected = option.isSelected;
                        }
                    });
                    e.selected = filter.selected;
                    if (e.selected) {
                        e.added = filter.added;
                        e.previousOptions = filter.previousOptions;
                    }
                    let addedFilters = currentVal?.filters.filter(
                        e => e.selected && e.added
                    );
                    if (addedFilters?.length > 0) {
                        let mostRecentAddedFilter = addedFilters.reduce(
                            (a, b) => (a.added > b.added ? a : b)
                        );
                        if (mostRecentAddedFilter.field == e.field) {
                            if (e.previousOptions && !e.isQueryable) {
                                e.options = e.previousOptions;
                                e.previousOptions = filter.previousOptions;
                            } else {
                                e.options = filter.options;
                                e.previousOptions = filter.options;
                            }
                        }
                    }

                    let mostRecentExtraFilter: IFilter;
                    const addedExtraFilters = filter.extraFilters?.filter(
                        e => e.added
                    );
                    if (addedExtraFilters?.length > 0) {
                        mostRecentExtraFilter = addedExtraFilters.reduce(
                            (a, b) => (a.added > b.added ? a : b)
                        );
                    }
                    e.extraFilters?.forEach(ex => {
                        let exFilter = filter.extraFilters?.find(
                            x => x.field == ex.field
                        );
                        if (exFilter) {
                            ex.selected = exFilter.selected;
                            if (ex.selected) {
                                ex.added = exFilter.added;
                                ex.previousOptions = exFilter.previousOptions;
                            }

                            ex.options?.forEach(o => {
                                let option = exFilter.options?.find(
                                    x => x.value == o.value
                                );
                                if (option) {
                                    o.isSelected = option.isSelected;
                                }
                            });

                            if (mostRecentExtraFilter?.field == ex.field) {
                                if (ex.previousOptions) {
                                    ex.options = ex.previousOptions;
                                    ex.previousOptions =
                                        exFilter.previousOptions;
                                } else {
                                    ex.options = exFilter.options;
                                    ex.previousOptions = exFilter.options;
                                }
                            }
                        }
                    });
                }
            });

            currentVal.filters = newFilters;
        }

        return currentVal;
    }

    /* 
    Adding a sort to config
    */
    public addSort(option: ISort): void {
        const currentVal = this.filterConfig;
        if (currentVal) {
            const sortVal = currentVal.sorts.find(
                e => e.sortType == option.sortType
            );
            if (sortVal) {
                sortVal.selected = true;
                sortVal.direction = option.direction;
                sortVal.added = new Date();
            }
        }
        this._filterConfig.next(currentVal);
    }

    /* 
    Removing a sort from config
    */
    public removeSort(option: ISort): void {
        const currentVal = this.filterConfig;
        if (currentVal) {
            const sortVal = currentVal.sorts.find(
                e => e.sortType == option.sortType
            );
            if (sortVal) {
                sortVal.selected = false;
                sortVal.direction = option.direction;
                sortVal.added = null;
            }
        }
        this._filterConfig.next(currentVal);
    }

    /* 
    Updating a sort in config
    */
    public updateSort(option: ISort): void {
        const currentVal = this.filterConfig;
        if (currentVal) {
            let sortVal = currentVal.sorts.find(
                e => e.sortType == option.sortType
            );
            if (sortVal) {
                sortVal = option;
            }
        }

        this._filterConfig.next(currentVal);
    }

    public async filterQueryEvent(
        filter: IFilter,
        searchQuery: string
    ): Promise<IFilterOption[]> {
        let query = this.generateQuery();

        query += `&${filter.queryableName}=${searchQuery}`;

        const response = await this.searchApiService
            .Search(this.filterConfig.endpoint, query)
            .toPromise();

        if (response.success) {
            const responseFilter: IFilter = response.filterWrapper.filters.find(
                t => t.field == filter.field
            );
            if (!responseFilter)
                throw new Error('Filter not found in search response');

            const configFilter: IFilter | undefined =
                this.filterConfig.filters.find(t => t.field == filter.field);

            if (!configFilter)
                throw new Error('Filter not found in filter config');

            return responseFilter.options.map(t => {
                t.isSelected = configFilter.options
                    .filter(t => t.isSelected)
                    .some(q => q.value == t.value);
                return t;
            });
        }
    }

    public async filterChangeEvent(config?: IFilterConfig): Promise<void> {
        this.dataLoading = true;
        let currentConfig = config ? config : this.filterConfig;
        let query = this.generateQuery();

        const response = await this.searchApiService
            .Search(this.filterConfig.endpoint, query)
            .toPromise();

        this.data = response.data as any[];
        this.totalResults = response.totalResults;

        const noActiveFilters =
            !currentConfig.filters ||
            !currentConfig.filters?.some(e => e.selected);

        if (noActiveFilters) {
            currentConfig.filters = response.filterWrapper?.filters;
        } else {
            currentConfig = this.refreshFilters(
                response.filterWrapper?.filters
            );
        }

        const noActiveSorts =
            !currentConfig.sorts || !currentConfig.sorts?.some(e => e.selected);
        if (noActiveSorts) {
            currentConfig.sorts = response.filterWrapper?.sorts.map(e => {
                if (e.default) {
                    e.selected = true;
                    e.added = new Date();
                    e.direction = e.defaultOrder;
                }
                return e;
            });
        }

        currentConfig.nextPageUrl = response.nextPage;
        currentConfig.previousPageUrl = response.previousPage;
        this.filterConfig = currentConfig;
        this.dataLoading = false;
    }

    public async loadNextPageEvent(): Promise<void> {
        let currentConfig = this.filterConfig;
        if (currentConfig.nextPageUrl) {
            const resp = await this.baseApiService
                .Get<
                    PagedCollectionFilterResponseWrapper<any>
                >(this.filterConfig.nextPageUrl, '')
                .toPromise();
            this.data = [...this.data, ...resp.data] as any[];
            currentConfig.nextPageUrl = resp.nextPage;
            currentConfig.previousPageUrl = resp.previousPage;
            this.filterConfig = currentConfig;
        }
    }

    public generateFilterQuery(query: string): string {
        const configVal = this._filterConfig.value;

        configVal?.filters
            ?.filter(e => e.selected)
            .forEach(e => {
                if (query) {
                    if (query.charAt(0) != '&') {
                        query += `&${e.field}=`;
                    }
                } else {
                    query += `${e.field}=`;
                }

                e.options
                    ?.filter(x => x.isSelected)
                    .forEach((x, index, arr) => {
                        // Changed: preserve spaces instead of removing them
                        const encodedValue = encodeURIComponent(x.value);

                        if (index == arr.length - 1) {
                            query += `${encodedValue}`;
                        } else {
                            query += `${encodedValue},`;
                        }
                    });

                if (e.extraFilters) {
                    e.extraFilters
                        ?.filter(z => z.selected)
                        .forEach(z => {
                            z.options
                                ?.filter(c => c.isSelected)
                                .forEach(c => {
                                    // Changed: preserve spaces instead of removing them
                                    query += `&${z.field}=${encodeURIComponent(c.value)}`;
                                });
                        });
                }
            });

        return query;
    }

    private generateSortQuery(query: string): string {
        const configVal = this._filterConfig.value;
        const activeSorts = configVal?.sorts
            ?.filter(x => x.selected)
            .sort((a, b) => (a.added < b.added ? 1 : -1));
        if (activeSorts) {
            if (query) {
                if (query.charAt(0) != '&') {
                    query += `&sorts=`;
                }
            } else {
                query += `sorts=`;
            }
            activeSorts.forEach((e, index, arr) => {
                query += `${e.sortType}:${e.direction}`;
                if (index != arr.length - 1) {
                    query += `,`;
                }
            });
        }

        return query;
    }
    private generateSearchQuery(query: string): string {
        const configVal = this._filterConfig.value;
        if (configVal.searchText?.trim()) {
            if (query) {
                if (query.charAt(0) != '&') {
                    query += configVal.endpoint.includes('productmanager-api')
                        ? `&queryTerm=${configVal.searchText}`
                        : `&query=${configVal.searchText}`;
                }
            } else {
                query += configVal.endpoint.includes('productmanager-api')
                    ? `queryTerm=${configVal.searchText}`
                    : `query=${configVal.searchText}`;
            }
        }
        return query;
    }

    private generatePaginationQuery(query: string): string {
        const configVal = this._filterConfig.value;
        if (query) {
            if (query.charAt(0) != '&') {
                query += `&page=${configVal.page}&pageSize=${configVal.pageSize}`;
            }
        } else {
            query += `page=${configVal.page}&pageSize=${configVal.pageSize}`;
        }
        return query;
    }

    get filterConfig$(): Observable<IFilterConfig> {
        return this._filterConfig.asObservable();
    }

    set filterConfig(obj: IFilterConfig) {
        this._filterConfig.next(obj);
    }

    get filterConfig(): IFilterConfig {
        return this._filterConfig.value;
    }

    get data$(): Observable<any[]> {
        return this._data.asObservable();
    }

    set data(obj: any[]) {
        this._data.next(obj);
    }

    get data(): any[] {
        return this._data.value;
    }

    get totalResults$(): Observable<number | null> {
        return this._totalResults.asObservable();
    }

    set totalResults(obj: number | null) {
        this._totalResults.next(obj);
    }

    get totalResults(): number | null {
        return this._totalResults.value;
    }

    get dataLoading$(): Observable<boolean> {
        return this._dataLoading.asObservable();
    }

    set dataLoading(val: boolean) {
        this._dataLoading.next(val);
    }

    set searchText(val: string) {
        let currentVal = this.filterConfig;
        currentVal.searchText = val;
        this.filterConfig = currentVal;
    }
}
